import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import {
  SIDEBAR_CLOSED_WIDTH,
  SIDEBAR_OPEN_WIDTH,
} from '#app/constants/sidenav.js';
import { useAddUserInterfaceFlag } from '#app/hooks/useAddUserInterfaceFlag.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { sessionStorage } from '#app/services/storage.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import { ChevronLeftIcon, CloseIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { captureException } from '@contra/sentry/react';
import { useCallback } from 'react';
import { css, styled, useTheme } from 'styled-components';

const GoToPortfolio = styled(Link)`
  display: block;
  position: absolute;
  left: 0;
  top: 8px;
  padding-left: 16px;
  height: 100%;
`;

const GoText = styled(Text)`
  display: flex;
  align-items: center;
  font-weight: 600;
  svg {
    display: block;
    margin-right: 8px;
    height: 16px;
    width: 16px;
  }
`;

const Layout = styled.div`
  ${({ theme }) => css`
    height: 64px;

    ${theme.mediaQueries.md} {
      height: 40px;
    }
  `}
`;

const StyledBanner = styled(Layout)<{ isSidenavDocked: boolean }>`
  ${({ isSidenavDocked, theme }) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background-color: rgba(221, 216, 255, 0.7);

    z-index: ${theme.zIndices.toolbar};

    ${theme.mediaQueries.lg} {
      height: 40px;
      left: ${isSidenavDocked ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH}px;
      width: calc(
        100% - ${isSidenavDocked ? SIDEBAR_OPEN_WIDTH : SIDEBAR_CLOSED_WIDTH}px
      );
    }
  `}
`;

const RelativeContainer = styled.div`
  line-height: 1.5;
  width: 100%;
  height: 100%;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colorsV2.black};
  text-align: center;
  padding: 8px 48px 8px 16px;
`;

const TextLink = styled.span`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    text-decoration: underline;
    margin-left: 6px;
  `}
`;

const CloseButton = styled.button`
  ${({ theme }) => css`
    color: ${theme.colorsV2.black};
    position: absolute;
    right: 16px;
    top: 8px;
  `}
`;

export const EditFromPortfolioBanner = ({
  isSidenavDocked,
}: {
  readonly isSidenavDocked: boolean;
}) => {
  const { track } = useAnalytics();
  const theme = useTheme();
  const currentUser = useCurrentUser();
  const isDesktopScreen = useMatchMedia(
    parseMediaQueryForMatchMedia(theme.mediaQueries.lg),
  );
  const [{ editFromPortfolio }] = useURLParameters('/@username');

  const { selectedUserType } = useSelectedUserType();

  const hideBannerInterfaceFlag = useHasUserInterfaceFlags(
    'HIDE_EDIT_FROM_PORTFOLIO_BANNER',
  );

  const [addUIFlag] = useAddUserInterfaceFlag();

  const handleClose = useCallback(() => {
    addUIFlag({
      onCompleted: () => {},
      onError: (error) => {
        captureException(error);
      },
      variables: {
        input: {
          userInterfaceFlag: 'HIDE_EDIT_FROM_PORTFOLIO_BANNER',
        },
      },
    });
  }, [addUIFlag]);

  if (
    !editFromPortfolio ||
    !currentUser?.profile?.id ||
    selectedUserType !== 'CONTRACTOR' ||
    hideBannerInterfaceFlag ||
    sessionStorage.getItem('CONTRA_SESSION_REDEEM_CODE_SUCCESS')
  ) {
    return null;
  }

  return (
    <>
      <StyledBanner isSidenavDocked={isSidenavDocked}>
        <RelativeContainer>
          {isDesktopScreen ? (
            <GoToPortfolio
              onClick={() => {
                track('link', {
                  action: 'tapped',
                  link_name: 'edit_from_portfolio_banner_link',
                });
              }}
              to={getRoute('/portfolio')}
            >
              <GoText textStyle="subtitleSmall">
                <ChevronLeftIcon />
                Go to portfolio
              </GoText>
            </GoToPortfolio>
          ) : null}
          <Text
            as="span"
            textStyle="bodySmall"
          >
            Changes to your profile will automatically be reflected on your
            <TextLink>
              <Link
                onClick={() => {
                  track('link', {
                    action: 'tapped',
                    link_name: 'edit_from_portfolio_banner_link',
                  });
                }}
                to={getRoute('/portfolio')}
              >
                portfolio
              </Link>
            </TextLink>
            .
          </Text>
          <CloseButton
            aria-label="Close banner"
            onClick={handleClose}
          >
            <CloseIcon />
          </CloseButton>
        </RelativeContainer>
      </StyledBanner>
      <Layout />
    </>
  );
};
